// Component style overrides
import { extendTheme } from '@chakra-ui/react'

import { buttonStyles } from './components/button'
import { drawerStyles } from './components/drawer'
import { formLabelStyles } from './components/form-label'
import { inputStyles } from './components/input'
import { switchStyles } from './components/switch'
import { tabStyles } from './components/tab'

export const SIDE_BAR_WIDTH_CLOSED = '60px'
export const SIDE_BAR_WIDTH_OPEN = '252px'
export const CALL_CONTAINER_Z_INDEX = 1401
// Above the Chakra v1 drawer layer (1400), below the 1500 modals — the dark
// Drawer's 1000 default renders the device drawer behind the incident drawer.
export const DEVICE_DRAWER_Z_INDEX = 1401
// Modals opened from a drawer. The tiering, low to high: app banners (1200),
// the Chakra v1 drawer layer (1400), the device drawer and call container
// (1401), these modals (1500), the FTU overlay (2100). The dark Modal's 1000
// default renders such a modal behind the drawer that spawned it, so every
// drawer-spawned modal must pass this.
export const MODAL_Z_INDEX = 1500

const fonts = {
  heading: 'Euclid Square',
  body: 'Euclid Square',
}

export const colors = {
  // Colour reference to Figma
  'darkblue-050': '#E6EAED',
  'darkblue-100': '#B3BFC6',
  'darkblue-200': '#80949F',
  'darkblue-300': '#64748B',
  'darkblue-400': '#264B5B',
  'darkblue-500': '#002233',
  'darkblue-600': '#001E2D',
  'darkblue-700': '#001824',
  'darkblue-800': '#00121B',
  'darkblue-900': '#000A0F',
  'duskblue-700': '#0C3145',
  'duskblue-800': '#092738',
  'ghostprotocol-500': '#8F8596',
  'signalyellow-300': '#E6FF81',
  'signalyellow-400': '#DDFE55',
  'signalyellow-500': '#D4FF29',
  'signalyellow-800': '#7A9400',
  'signalyellow-900': '#576B00',
  'checkbox-selected': {
    500: '#092738',
  },

  'tailgate-overlay': {
    'label-background': {
      light: 'rgba(0, 0, 0, 0.75)',
      dark: 'rgba(0, 0, 0, 0.75)',
    },
    'label-text': {
      light: 'rgba(255, 255, 255, 0.95)',
      dark: 'rgba(255, 255, 255, 0.95)',
    },
    'neutral-track': {
      light: 'rgba(236, 201, 75, 0.95)',
      dark: 'rgba(236, 201, 75, 0.95)',
    },
    secured: {
      fill: {
        light: 'rgba(245, 101, 101, 0.22)',
        dark: 'rgba(245, 101, 101, 0.22)',
      },
      stroke: {
        light: 'rgba(245, 101, 101, 0.9)',
        dark: 'rgba(245, 101, 101, 0.9)',
      },
      track: {
        light: 'rgba(245, 101, 101, 0.95)',
        dark: 'rgba(245, 101, 101, 0.95)',
      },
    },
    unsecured: {
      fill: {
        light: 'rgba(72, 187, 120, 0.22)',
        dark: 'rgba(72, 187, 120, 0.22)',
      },
      stroke: {
        light: 'rgba(72, 187, 120, 0.9)',
        dark: 'rgba(72, 187, 120, 0.9)',
      },
      track: {
        light: 'rgba(72, 187, 120, 0.95)',
        dark: 'rgba(72, 187, 120, 0.95)',
      },
    },
  },

  brand: {
    50: '#3369A8',
    100: '#2E6CDB',
    150: '#7FABFF',
    175: '#a9bbfa',
    200: '#6096F5',
    250: '#004492',
    300: '#e4effb',
    400: '#2D2E41',
    500: '#353849',
    600: '#8E919F',
    700: '#1CFDE5',
    800: '#07DB94',
    900: '#3279C7',

    /* region NEW Brand Colours */
    primary: {
      light: '#14B8A6',
      dark: '#DDFE55',
    },
    glow: {
      light: null,
      dark: 'Dawn Blue 400',
    },
    dark: {
      light: '1A2538',
      dark: 'Dark Blue 900',
    },
    overwatch: {
      light: 'E8F0FE',
      dark: 'TBD',
    },
  },
  icon: {
    primary: {
      light: '#FFFFFF',
      dark: '#E6EAED',
    },
    secondary: {
      light: '#94A3B8',
      dark: '#64748B',
    },
    active: {
      light: '#14B8A6',
      dark: '#DDFE55',
    },
    danger: {
      light: '#EF4444',
      dark: '#ED3E4C', // red/400 (design-library primitive)
    },
    'on-accent': {
      light: '#FFFFFF',
      dark: 'Dark Blue 900',
    },
    disabled: {
      light: '#D1D5DB',
      dark: 'Ghost Protocol 600',
    },
    // Media navigation arrows sit on top of photos/video stills, so like
    // text.on-overlay they keep the same value in both modes.
    'media-nav': {
      light: '#000000',
      dark: '#000000',
    },
    'media-nav-disabled': {
      light: '#FFFFFF',
      dark: '#FFFFFF',
    },
    topbar: {
      light: '#475569',
      dark: '#E6EAED',
    },
    breadcrumb: {
      light: '#979595',
      dark: '#FFFFFF',
    },
    // Form builder info banner icon
    'info-banner': {
      light: '#2563EB', // Blue 600
      dark: '#93C5FD', // Blue 300
    },
  },
  /* region NEW Shadow Colours */
  shadow: {
    card: {
      light: '0 1px 3px rgba(0,0,0,0.1)',
      dark: '0 4px 16px rgba(0,0,0,0.3)',
    },
    elevated: {
      light: '0 4px 12px rgba(0,0,0,0.15)',
      dark: '0 8px 32px rgba(0,0,0,0.5)',
    },
    glow: {
      light: null,
      dark: 'Dawn Blue 400 (15% opacity / 20px blur)',
    },
    dropdown: {
      light: '0 2px 8px rgba(0,0,0,0.12)',
      dark: '0 4px 12px rgba(0,0,0,0.4)',
    },
    'focus-ring': {
      light: '0 0 0 2px rgba(20, 184, 166, 0.2)', // Teal with opacity
      dark: '0 0 0 2px rgba(221, 254, 85, 0.3)', // Signal Yellow with opacity
    },
    /* endregion */
  },
  bg: {
    100: '#F0F5FD',
    200: '#C9DBF8',
    300: '#DCE8FA',
    400: '#F8E5E8',
    500: '#F3F3F3',
    550: '#EEF4FC',
    600: '#66666E',

    /* region NEW Bg Colours */
    app: {
      light: '#F0F1F5',
      dark: '#000A0F',
    },
    skeleton: {
      light: '#D9D9D9',
      dark: '#092738',
    },
    sidebar: {
      light: '#E2E8F0',
      // Dark Blue 500
      dark: '#002233',
    },
    'sidebar-hover': {
      light: '#1E2F45',
      dark: '#001E2D',
    },
    'sidebar-active': {
      light: '#1C283F',
      dark: 'transparent',
    },
    card: {
      light: '#F0F1F5',
      dark: '#00121B',
    },
    'card-elevated': {
      light: '#FFFFFF',
      dark: '#001824',
    },
    'card-elevated-secondary': {
      light: '#F0F1F5',
      dark: '#001E2D',
    },
    input: {
      light: '#FFFFFF',
      dark: '#092738',
    },
    'schedule-block': {
      light: '#9BA8B759',
      dark: '#264B5B80',
    },
    // Ground the floor-plan canvas sits on. The light value matches the global
    // body background in styles/global.tsx so the canvas is unchanged in light
    // mode; declaring it here is what lets the floor plan go dark without
    // touching that body rule, which every other page shares.
    map: {
      light: '#F0F5FD',
      dark: '#000A0F',
    },
    'table-row': {
      light: '#FFFFFF',
      dark: 'Dark Blue 900',
    },
    'table-row-alt': {
      light: '#F8F9FC',
      dark: '#00121B',
    },
    'table-row-hover': {
      light: '#EDF2F7',
      dark: '#00121B',
    },
    overlay: {
      light: 'rgba(0,0,0,0.4)',
      // Was the placeholder name 'Black (60% opacity)' — an invalid
      // declaration that rendered no overlay at all in dark mode.
      dark: 'rgba(0,0,0,0.6)',
    },
    topbar: {
      light: '#FFFFFF',
      dark: '#001824',
    },
    chip: {
      light: '#374151',
      dark: '#DDFE55',
    },
    'chip-label': {
      light: '#FFFFFF',
      dark: '#264B5B',
    },
    'alert-info': {
      light: '#EBF5FF',
      dark: '#051A25', // duskblue/900 (design-library primitive)
    },
    'alert-warning': {
      light: '#FEF3C7', // status/warning-bg (design-library semantic)
      dark: '#4D1F00',
    },
    'alert-success': {
      light: '#ECFDF5',
      dark: '#14532D', // Green 900
    },
    'alert-danger': {
      light: '#7F1D1D',
      dark: '#3D0D0F', // red/900 (design-library primitive)
    },
    'toggle-track': {
      light: '#D1D5DB',
      dark: 'Ghost Protocol 700',
    },
    'sidebar-account': {
      light: '#9DABC8',
      dark: '#264B5B',
    },
    'user-badge': {
      light: '#31405E',
      dark: 'Dawn Blue 300',
    },
    'tab-active': {
      light: '#FFFFFF',
      dark: '#264B5B',
    },
    divider: {
      light: '#264B5B',
      dark: '#E2E8F0',
    },
    'divider-dark': {
      light: '#E2E8F0',
      dark: '#001E2D',
    },
    disabled: {
      light: '#F3F4F6',
      dark: '#00121B',
    },
    'filters-btn': {
      light: '#14B8A6',
      dark: '#00121B',
    },
    // Form builder indicator backgrounds
    orphaned: {
      light: '#FEF3C7', // Amber 100
      dark: '#78350F', // Amber 900
    },
    trigger: {
      light: '#F5F3FF', // Violet 50
      dark: '#2E1065', // Violet 950
    },
    /* endregion */
  },
  btn: {
    100: '#548BEF',
    200: '#96ABD2',
    'change-roles': {
      light: '#6B46C1',
      dark: '#9F7AEA',
    },
    'change-roles-hover': {
      light: '#553C9A',
      dark: '#B794F4',
    },
    edit: {
      light: '#E5E8EE',
      dark: '#2D3748',
    },
    'edit-hover': {
      light: '#d2d2d4',
      dark: '#4A5568',
    },
    offboard: {
      light: '#D01030',
      dark: '#FC8181',
    },
    'offboard-hover': {
      light: '#b40e2a',
      dark: '#FEB2B2',
    },
  },
  border: {
    100: '#E2E8F0',
    200: '#DBDBDB',
    300: '#BBB',
    400: '#FBC4C3',
    500: '#E9E9EB',
    /* region NEW Border Colours */
    default: {
      light: '#E2E8F0',
      dark: '#E2E8F0',
    },
    subtle: {
      light: '#CFD6E2',
      dark: '#0C3145',
    },
    strong: {
      light: '#CBD5E1',
      dark: '#7D7185',
    },
    glow: {
      light: null,
      dark: 'Dawn Blue 400',
    },
    input: {
      light: '#D1D5DB',
      dark: '#6B5D74',
    },
    'input-hover': {
      light: '#CBD5E1',
      dark: '#0C3145',
    },
    'input-focus': {
      light: '#14B8A6',
      dark: '#264B5B',
    },
    'input-error': {
      light: '#DC2626',
      dark: '#A62630',
    },
    error: {
      light: '#DC2626',
      dark: 'Red 500',
    },
    chip: {
      light: '#D1D5DB',
      dark: 'Dawn Blue 600',
    },
    search: {
      light: '#E2E8F0',
      dark: 'Ghost Protocol 700',
    },
    topbar: {
      light: '#E2E8F0',
      dark: '#002233',
    },
    'tab-toggle': {
      light: '#D1D5DB',
      dark: '#264B5B',
    },
    'card-elevated-secondary': {
      light: '#E2E8F0',
      dark: '#264B5B',
    },
    // Form builder indicator borders
    visibility: {
      light: '#10B981', // Emerald 500
      dark: '#22C55E', // Green 500
    },
    nested: {
      light: '#3B82F6', // Blue 500
      dark: '#60A5FA', // Blue 400
    },
    'info-banner': {
      light: '#3B82F6', // Blue 500
      dark: '#60A5FA', // Blue 400
    },
    trigger: {
      light: '#A78BFA', // Violet 400
      dark: '#7C3AED', // Violet 600
    },
    /* endregion */
  },
  link: { 100: '#3D7FCE' },
  incident: {
    active: {
      100: '#D01030',
      200: '#b40e2a',
    },
    progress: {
      100: '#D16A1E',
    },
    resolved: {
      100: '#29D11E',
    },
  },
  components: {
    switch: {
      active: '#2fb2b0',
      inactive: '#931100',
    },
    tab: {
      active: '#005BBF',
    },
  },
  success: {
    100: '#009a0d',
  },
  alert: {
    100: '#fd0e0a',
  },
  sidebar: {
    darkText: '#091848',
    avatarBg: '#495870',
    // Collapse/expand toggle chevron — light so it reads on the dark sidebar
    // surface the toggle now matches.
    icon: {
      light: '#FFFFFF',
      dark: '#4d6978',
    },
    'icon-text': {
      light: '#091848',
      dark: '#FFFFFF',
    },
  },
  /* region NEW Text Colours */
  text: {
    primary: {
      light: '#1E293B',
      dark: '#E6EAED',
    },
    secondary: {
      light: '#64748B',
      dark: '#4D6978',
    },
    header: {
      light: '#264B5B',
      dark: '#4D6978',
    },
    tertiary: {
      light: '#94A3B8',
      dark: '#8F8596',
    },
    'input-value': {
      light: '#374151',
      dark: '#80949F',
    },
    // 'input-placeholder': {
    //   light: '#374151',
    //   dark: '#80949F',
    // },
    'input-disabled': {
      light: '#9CA3AF',
      dark: '#264B5B',
    },
    disabled: {
      light: '#CBD5E1',
      dark: 'Ghost Protocol 600',
    },
    'on-accent': {
      light: '#FFFFFF',
      dark: 'Dark Blue 900',
    },
    'on-danger': {
      light: '#FFFFFF',
      dark: '#E6EAED',
    },
    // For text/indicators sitting on a dark scrim (e.g. an image overlay).
    // Stays light in both modes since the scrim is dark in both.
    'on-overlay': {
      light: '#FFFFFF',
      dark: '#E6EAED',
    },
    'on-overlay-muted': {
      light: 'rgba(255, 255, 255, 0.4)',
      dark: 'rgba(230, 234, 237, 0.4)',
    },
    link: {
      light: '#0891B2',
      dark: 'Dawn Blue 300',
    },
    'badge-count': {
      light: '#FFFFFF',
      dark: '#E6EAED',
    },
    'chip-label': {
      light: '#E8EDF2',
      dark: 'Dark Blue 900',
    },
    'alert-info': {
      light: '#1E40AF',
      dark: '#C0D6EA', // dawnblue/200 (status/info-text; per the Alert dark spec)
    },
    'alert-warning': {
      // Text on bg.alert-warning surfaces: amber 700 for contrast on the pale
      // amber banner, status/warning-text on the dark amber banner
      light: '#B45309',
      dark: '#FFBF97',
    },
    'alert-info-bold': {
      light: '#1E3A5A',
      dark: '#E6EAED',
    },
    'alert-success': {
      light: '#166534',
      dark: '#BBF7D0', // Green 200
    },
    'alert-success-bold': {
      light: '#14532D',
      dark: '#DCFCE7', // Green 100
    },
    'alert-danger': {
      light: '#DC2626',
      dark: '#FCA5A5',
    },
    'alert-danger-bold': {
      // Headline text on bg.alert-danger surfaces, per the Alert dark spec:
      // white on the legacy red bar, red/050 on the dark red banner
      light: '#FFFFFF',
      dark: '#FDEAEC',
    },
    'nav-default': {
      light: '#FFFFFF',
      dark: '#64748B',
    },
    'nav-active': {
      light: '#FFFFFF',
      dark: '#DDFE55',
    },
    'page-title': {
      light: '#1E293B',
      dark: '#E6EAED',
    },
    'count-badge': {
      light: '#FFFFFF',
      dark: '#002233',
    },
    'chip-label-secondary': {
      light: '#64748B',
      dark: '#B3BFC6',
    },
    'btn-primary': {
      light: '#FFFFFF',
      dark: '#002233',
    },
    // Form builder indicator text
    orphaned: {
      light: '#92400E', // Amber 800
      dark: '#FDE68A', // Amber 200
    },
    trigger: {
      light: '#5B21B6', // Violet 800
      dark: '#C4B5FD', // Violet 300
    },
    // Warning text with good contrast on light backgrounds
    warning: {
      light: '#B45309', // Amber 700 - 4.5:1 contrast
      dark: '#FBBF24', // Amber 400
    },
  },
  /* endregion */
  /* region NEW Interactive Colours */
  interactive: {
    primary: {
      light: '#14B8A6',
      dark: '#DDFE55',
    },
    'primary-hover': {
      light: '#0D9488',
      dark: 'Signal Yellow 300',
    },
    'primary-active': {
      light: '#0F766E',
      dark: 'Signal Yellow 500',
    },
    'primary-disabled-bg': {
      light: '#99F6E4',
      dark: 'Signal Yellow 900',
    },
    'primary-disabled-text': {
      light: '#5EEAD4',
      dark: 'Signal Yellow 800',
    },
    danger: {
      light: '#DC2626',
      dark: 'Red 500',
    },
    'danger-hover': {
      light: '#EF4444',
      dark: 'Red 400',
    },
    'danger-active': {
      light: '#B91C1C',
      dark: 'Red 600',
    },
    'danger-disabled-bg': {
      light: '#FCA5A5',
      dark: 'Red 900',
    },
    'danger-disabled-text': {
      light: '#F87171',
      dark: 'Red 800',
    },
    'secondary-border': {
      light: '#D1D5DB',
      dark: '#E6EAED',
    },
    'secondary-text': {
      light: '#374151',
      dark: '#E6EAED',
    },
    'secondary-hover-bg': {
      light: 'transparent',
      dark: 'Dark Blue 700 (10% opacity)',
    },
    'secondary-hover-border': {
      light: '#9CA3AF',
      dark: 'Ghost Protocol 300',
    },
    'secondary-active-bg': {
      light: 'transparent',
      dark: 'Dark Blue 600 (15% opacity)',
    },
    'secondary-active-border': {
      light: '#6B7280',
      dark: 'Ghost Protocol 400',
    },
    'secondary-active-text': {
      light: '#1F2937',
      dark: '#E6EAED',
    },
    'secondary-disabled-border': {
      light: '#E5E7EB',
      dark: 'Ghost Protocol 700',
    },
    'secondary-disabled-text': {
      light: '#D1D5DB',
      dark: 'Ghost Protocol 600',
    },
    'tertiary-border': {
      light: '#E2E8F0',
      dark: '#0C3145',
    },
    'tertiary-text': {
      light: '#64748B',
      dark: 'Ghost Protocol 400',
    },
    'tertiary-hover-bg': {
      light: 'transparent',
      dark: 'Dusk Blue 800 (10% opacity)',
    },
    'tertiary-hover-border': {
      light: '#CBD5E1',
      dark: 'Dusk Blue 600',
    },
    'tertiary-active-bg': {
      light: 'transparent',
      dark: 'Dusk Blue 700 (15% opacity)',
    },
    'tertiary-active-border': {
      light: '#9CA3AF',
      dark: 'Dusk Blue 500',
    },
    'tertiary-active-text': {
      light: '#475569',
      dark: 'Ghost Protocol 300',
    },
    'tertiary-disabled-border': {
      light: '#F1F5F9',
      dark: '#092738',
    },
    'tertiary-disabled-text': {
      light: '#CBD5E1',
      dark: 'Ghost Protocol 700',
    },
    'focus-ring': {
      light: '#14B8A6',
      dark: 'Dawn Blue 400',
    },
    'toggle-on': {
      light: '#14B8A6',
      dark: '#DDFE55',
    },
    'nav-active': {
      light: null,
      dark: '#DDFE55',
    },
    'selected-row': {
      light: '#EBF5FF',
      dark: 'Dawn Blue 900',
    },
    'selected-hover': {
      light: '#0D9488', // Teal 600
      dark: '#DDFE55', // Signal Yellow
    },
  },

  /* region NEW Status Colours */
  status: {
    success: {
      light: '#00C054',
      dark: '#00C054', // Green 500
    },
    'success-bg': {
      light: '#003D1C',
      dark: '#14532D', // Green 900
    },
    'success-icon': {
      light: '#4DE6A0',
      dark: '#86EFAC', // Green 300
    },
    warning: {
      light: '#E8CC00',
      dark: '#C35000', // Yellow 500
    },
    'warning-bg': {
      light: '#4D3D00',
      dark: '#713F12', // Yellow 900
    },
    error: {
      light: '#D0303C',
      dark: '#d0303c', // Red 500
    },
    'error-bg': {
      light: '#3D0D0F',
      dark: '#7F1D1D', // Red 900
    },
    info: {
      light: '#8FB8DC',
      dark: '#60A5FA', // Blue 400
    },
    'info-bg': {
      light: '#2A4F70',
      dark: '#1E3A5F', // Dusk Blue 900
    },
    'info-icon': {
      light: '#11425D',
      dark: '#3B82F6', // Blue 500
    },
    neutral: {
      light: '#8F8596',
      dark: '#8F8596',
    },
    'neutral-bg': {
      light: '#564A5D',
      dark: '#374151', // Gray 700
    },
    'incident-badge': {
      light: '#E6EAED',
      dark: '#E6EAED',
    },
    'incident-badge-topbar': {
      light: '#DDFE55',
      dark: '#DDFE55',
    },
    'guard-badge': {
      light: '#14B8A6',
      dark: 'TBD',
    },
    'sop-operator': {
      light: '#1E3A5A',
      dark: 'TBD',
    },
    'sop-guard': {
      light: '#14B8A6',
      dark: 'TBD',
    },
    // Form builder badge backgrounds with good contrast for white text (4.5:1)
    'success-badge': {
      light: '#059669', // Emerald 600
      dark: '#10B981', // Emerald 500
    },
    'info-badge': {
      light: '#2563EB', // Blue 600
      dark: '#3B82F6', // Blue 500
    },
  },
}

const components = {
  Tooltip: {
    baseStyle: {
      borderRadius: 'md',
    },
  },
  Button: buttonStyles,
  Input: inputStyles,
  Drawer: drawerStyles,
  FormLabel: formLabelStyles,
  Switch: switchStyles,
  Modal: {
    parts: ['overlay', 'item'],
    baseStyle: {
      overlay: {
        background: 'rgba(48,56,78,0.7)',
      },
    },
  },
  Skeleton: {
    baseStyle: {
      borderRadius: 'md',
    },
  },
  Tabs: tabStyles,
}

const shadows = {
  outline: 'none',
}

export const theme = extendTheme({
  shadows,
  colors,
  fonts,
  components,
})
